import {
  EBenefitCategory,
  ECareType,
  EClaimType,
  IApplicableBenefit,
  IHealthInsuranceMemberAndDependents,
  IMember,
  IMemberBenefit,
  IQuery,
} from '@/types';
import { decodeAuthToken, gatewayAxiosInstance } from '@/utils';
import getDateObject from '@/utils/getDateObject';
import sleep from '@/utils/sleep';
import { isValid } from 'date-fns';

export const memberDetailsQuery = (
  typeOfClaim?: EClaimType,
  categories?: Array<EBenefitCategory>,
): IQuery<Array<IMember>> => {
  const { organisationId, memberId } = decodeAuthToken() ?? {};
  return {
    queryKey: ['memberDetails', organisationId, memberId, typeOfClaim ?? 'all'],
    queryFn: async () => {
      if (!organisationId || !memberId) {
        return [];
      }
      let url = `/members/organisation/${organisationId}/member/${memberId}/active-members?organisationId=${organisationId}&includeExpiredBenefits=true`;
      if (Array.isArray(categories) && (categories?.length ?? 0) > 0) {
        url = `${url}&benefitCategory=${categories?.toString()}`;
      } else {
        url = `${url}&benefitCategory=insurance,expense-management`;
      }
      if (typeOfClaim) {
        url = `${url}&typeOfClaim=${typeOfClaim}`;
      }
      const memberDetails = await gatewayAxiosInstance.get<Array<IMember>>(url);
      return memberDetails.data;
    },
  };
};

export const getApplicableBenefits = ({
  date,
  patientId,
  typeOfCare,
  typeOfClaim,
  benefitCategory = [],
}: {
  typeOfClaim: EClaimType;
  typeOfCare: ECareType;
  patientId: string;
  date: Date;
  benefitCategory?: Array<EBenefitCategory>;
}): IQuery<Array<IApplicableBenefit> | null> => {
  const { organisationId, memberId } = decodeAuthToken() ?? {};
  const dateObject = getDateObject(date);
  const dateString = isValid(date)
    ? `${dateObject?.day}-${dateObject?.month}-${dateObject?.year}`
    : null;
  return {
    queryKey: ['applicableBenefits', organisationId, memberId],
    queryFn: async () => {
      if (!organisationId || !memberId) {
        return null;
      }
      let url = `/members/organisation/${organisationId}/member/${memberId}/get-applicable-org-benefit-info?typeOfClaim=${typeOfClaim}&typeOfCare=${typeOfCare}&patientId=${patientId}&organisationId=${organisationId}&dateOfAdmissionStr=${dateString}`;
      if (benefitCategory?.length > 0) {
        url = `${url}&benefitCategory=${benefitCategory.toString()}`;
      }
      if (organisationId && memberId) {
        const applicableBenefits =
          await gatewayAxiosInstance.get<Array<IApplicableBenefit>>(url);
        return applicableBenefits.data;
      }
      return null;
      return await sleep(200, [
        {
          orgBenefitId: 'offline',
          tpaName: 'offline',
          insuranceProviderHandle: 'offline',
          insuranceProviderPartner: 'offline',
          typeOfBenefit: 'offline',
        },
      ]);
    },
  };
};

export const getAllBenefitsForMember = (
  categories?: Array<EBenefitCategory | string>,
): IQuery<Array<IMemberBenefit>> => {
  const { organisationId, memberId } = decodeAuthToken() ?? {};
  return {
    queryKey: ['allBenefits', organisationId, memberId, categories?.toString()],
    queryFn: async () => {
      if (!organisationId || !memberId) {
        return [];
      }
      let url = `/core/organisation/${organisationId}/member/${memberId}/get-all-benefits-for-member?skipExpiredBenefits=true`;
      if ((categories?.length ?? 0) > 0) {
        url = `${url}&category=${categories?.toString()}`;
      }
      const allBenefits = await gatewayAxiosInstance.get<{
        benefits: Array<IMemberBenefit>;
      }>(url);
      return allBenefits.data.benefits;
    },
  };
};


export const getMemberAndDependentsDetails = (benefitId: string): IQuery<IHealthInsuranceMemberAndDependents> => {
  const { organisationId, memberId } = decodeAuthToken() ?? {};
  return {
    queryKey: ['memberAndDependentsDetails', organisationId, memberId],
    queryFn: async () => {
      const url = `/core/organisation/${organisationId}/benefit/${benefitId}/member/${memberId}/get-member-and-dependent-details`;
      const memberAndDependentsDetails = await gatewayAxiosInstance.get<IHealthInsuranceMemberAndDependents>(url);
      return memberAndDependentsDetails.data;
    },
  };
};